import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUrl';

const API_BASE = process.env.REACT_APP_API_URL;

// --- SHARED COMPONENTS ---
const CardSlider = ({ images }) => {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        if (!images || images.length <= 1) return;
        const interval = setInterval(() => setIndex(prev => (prev + 1) % images.length), 3500);
        return () => clearInterval(interval);
    }, [images]);
    return (
        <div className="relative h-full w-full overflow-hidden">
            {images?.map((img, i) => (
                <img key={i} src={getImageUrl(img, API_BASE)} loading="lazy" decoding="async" width="400" height="320" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === index ? 'opacity-100' : 'opacity-0'}`} alt="Project" />
            ))}
        </div>
    );
};

export default function Home({ lang, t }) {
    const [projects, setProjects] = useState([]);
    const [techs, setTechs] = useState([]);
    const [customers, setCustomers] = useState([]); // NEW STATE
    const [selectedProject, setSelectedProject] = useState(null);
    const [fullscreenImg, setFullscreenImg] = useState(null);
    const [form, setForm] = useState({ name: '', email: '', message: '' });

    useEffect(() => {
        const fetchData = async () => {
            // Fetch independently so one failure doesn't blank the entire page
            try { const p = await axios.get(`${API_BASE}/api/projects`); setProjects(p.data); } catch (err) { console.error('Projects fetch failed:', err); }
            try { const te = await axios.get(`${API_BASE}/api/technologies`); setTechs(te.data); } catch (err) { console.error('Tech fetch failed:', err); }
            try { const cu = await axios.get(`${API_BASE}/api/customers`); setCustomers(cu.data); } catch (err) { console.error('Customers fetch failed:', err); }
        };
        fetchData();
        window.scrollTo(0, 0);
    }, []);

    const sendTicket = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_BASE}/api/tickets`, form);
            alert("Sent!");
            setForm({ name: '', email: '', message: '' });
        } catch (err) { console.error(err); }
    };

    if (fullscreenImg) return <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={() => setFullscreenImg(null)}><img src={getImageUrl(fullscreenImg, API_BASE)} className="max-w-full max-h-full rounded shadow-2xl" alt="Full" /></div>;

    if (selectedProject) {
        const p = selectedProject;
        return (
            <div className="container mx-auto px-6 py-12 animate-fadeIn min-h-screen">
                <button onClick={() => { setSelectedProject(null); window.scrollTo(0, 0); }} className="mb-8 font-black text-blue-700 uppercase">← {t.back || 'Back'}</button>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                    <div className="space-y-6">
                        <h1 className="text-5xl font-black text-slate-900 uppercase italic">{p.title[lang]}</h1>
                        <p className="text-xl text-slate-600 italic">{p.desc[lang]}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {p.images.map((img, idx) => (
                            <img key={idx} src={getImageUrl(img, API_BASE)} loading="lazy" decoding="async" width="400" height="400" className="w-full aspect-square object-cover rounded-2xl shadow-lg cursor-zoom-in" onClick={() => setFullscreenImg(img)} alt="Detail" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            <Helmet>
                <title>Alum Nawatha LTD — Premium Aluminum Windows, Doors & Facades | Since 1986</title>
                <meta name="description" content="Alum Nawatha LTD — 40+ years of precision aluminum manufacturing. Custom windows, doors, facades & curtain walls in Northern Israel." />
            </Helmet>
            {/* HERO */}
            <section className="relative h-[85vh] flex items-center justify-center text-center bg-slate-950 overflow-hidden">
                <div className="absolute inset-0 opacity-40">
                    <img src="/assets/hero-industry.jpg" fetchpriority="high" width="1920" height="1080" className="w-full h-full object-cover" alt="Alum Nawatha industrial facility" />
                </div>
                <div className="relative z-10 px-6 flex flex-col items-center">
                    <img src="/assets/logo-circle.png" width="160" height="160" className="w-28 h-28 md:w-40 md:h-40 mb-10 drop-shadow-2xl animate-pulse" alt="Alum Nawatha seal" />
                    <h1 className="text-5xl md:text-8xl font-black text-white mb-6 tracking-tighter uppercase italic leading-[0.9]">{t.hero}</h1>
                    <p className="text-blue-400 font-bold tracking-[0.4em] text-xs uppercase italic">ALUM NAWATHA LTD | {t.experience}</p>
                </div>
            </section>

            {/* CUSTOMER RIBBON - PRO ADDITION */}
            <section className="py-16 bg-white border-b border-slate-100 overflow-hidden group">
                <div className="container mx-auto px-6 mb-8 text-center">
                    <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.5em] mb-2">Our Strategic Partners</p>
                    <div className="w-8 h-1 bg-slate-200 mx-auto"></div>
                </div>
                <div className="flex overflow-hidden relative">
                    <div className="flex animate-loop-scroll space-x-20 items-center whitespace-nowrap">
                        {[...customers, ...customers].map((c, i) => (
                            <div key={i} className="flex flex-col items-center min-w-[150px] grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-500">
                                <img src={getImageUrl(c.image, API_BASE)} loading="lazy" decoding="async" width="150" height="56" className="h-10 md:h-14 w-auto object-contain mb-3" alt={c.name} />
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{c.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROJECTS */}
            <section id="projects" className="py-24 container mx-auto px-6">
                <h2 className="text-4xl font-black text-slate-900 uppercase italic text-center mb-16">{t.projects}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                    {projects.map(p => (
                        <div key={p.id} onClick={() => { setSelectedProject(p); window.scrollTo(0, 0); }} className="bg-white rounded-[2.5rem] shadow-xl overflow-hidden hover:-translate-y-3 transition-all cursor-pointer border border-slate-50 group">
                            <div className="h-80 relative"><CardSlider images={p.images} /></div>
                            <div className="p-10">
                                <h3 className="text-2xl font-black text-slate-900 mb-4 uppercase italic group-hover:text-blue-700">{p.title[lang]}</h3>
                                <p className="text-slate-500 line-clamp-2 text-sm italic mb-6">{p.desc[lang]}</p>
                                <span className="text-blue-700 font-black text-xs tracking-widest uppercase">EXPLORE →</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* TECHNOLOGY */}
            <section className="bg-slate-900 py-24 px-6 border-y border-slate-800">
                <div className="container mx-auto">
                    <h2 className="text-4xl font-black text-white uppercase italic text-center mb-16">{t.tech}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl mx-auto">
                        {techs.map(te => (
                            <div key={te.id} className="bg-slate-800/50 p-4 rounded-[2rem] border border-slate-700 flex flex-col md:flex-row gap-8 items-center hover:bg-slate-800 transition group">
                                {te.images?.[0] && <img src={getImageUrl(te.images[0], API_BASE)} loading="lazy" decoding="async" width="192" height="192" className="w-48 h-48 object-cover rounded-2xl group-hover:scale-105 transition" alt="Tech" />}
                                <div>
                                    <h3 className="text-2xl font-black text-blue-400 mb-2 uppercase italic">{te.title[lang]}</h3>
                                    <p className="text-slate-400 text-sm italic">{te.desc[lang]}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONTACT */}
            <section id="contact" className="py-24 container mx-auto px-6">
                <div className="max-w-5xl mx-auto bg-slate-100 rounded-[4rem] overflow-hidden shadow-2xl flex flex-col lg:flex-row border border-white">
                    <div className="lg:w-2/5 bg-slate-900 p-16 text-white flex flex-col justify-center">
                        <h2 className="text-4xl font-black mb-8 uppercase italic">{t.contact}</h2>
                        <p className="text-slate-400 text-sm italic">{lang === 'en' ? 'Contact our team for premium aluminum engineering.' : 'צור קשר עם הצוות שלנו להנדסת אלומיניום איכותית.'}</p>
                    </div>
                    <div className="lg:w-3/5 p-16 bg-white">
                        <form onSubmit={sendTicket} className="space-y-8">
                            <input required placeholder={t.name} className="w-full bg-slate-50 border-b-2 border-slate-100 p-4 outline-none focus:border-blue-700" onChange={e => setForm({ ...form, name: e.target.value })} />
                            <input required type="email" placeholder={t.email} className="w-full bg-slate-50 border-b-2 border-slate-100 p-4 outline-none focus:border-blue-700" onChange={e => setForm({ ...form, email: e.target.value })} />
                            <textarea required placeholder={t.msg} rows="4" className="w-full bg-slate-50 border-b-2 border-slate-100 p-4 outline-none focus:border-blue-700" onChange={e => setForm({ ...form, message: e.target.value })}></textarea>
                            <button className="bg-slate-900 text-white w-full py-6 rounded-2xl font-black uppercase tracking-[0.3em] hover:bg-blue-700 transition">
                                {t.send}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
}